import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck, FileDown, RefreshCw, Loader2, ChevronDown, ChevronRight,
  CheckCircle2, Circle, ExternalLink, Sparkles, Award, ClipboardCheck, AlertTriangle,
  History, Mail, Archive, Send, Wand2, Check, X, Zap, StopCircle, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger
} from '../components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PILLAR_ORDER = [
  'Safety Policy',
  'Safety Accountability, Authority & Responsibilities',
  'Safety Risk Management',
  'Safety Assurance',
  'Safety Promotion',
  'SMS Documentation & Recordkeeping',
];

const STATES = [
  { key: 'present',   label: 'Present'   },
  { key: 'suitable',  label: 'Suitable'  },
  { key: 'operating', label: 'Operating' },
  { key: 'effective', label: 'Effective' },
];

const maturityClass = (label) => {
  if (label?.startsWith('N5')) return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40';
  if (label?.startsWith('N4')) return 'bg-sky-500/15 text-sky-300 border-sky-500/40';
  if (label?.startsWith('N3')) return 'bg-amber-500/15 text-amber-300 border-amber-500/40';
  if (label?.startsWith('N2')) return 'bg-orange-500/15 text-orange-300 border-orange-500/40';
  if (label?.startsWith('N1')) return 'bg-rose-500/15 text-rose-300 border-rose-500/40';
  return 'bg-slate-700/40 text-slate-300 border-slate-700';
};

export default function GACAAuditPage({ onNavigate }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [linkedCapas, setLinkedCapas] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [savingMetadata, setSavingMetadata] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkStarting, setBulkStarting] = useState(false);
  const token = localStorage.getItem('token');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [chk, sc, lc] = await Promise.all([
        fetch(`${API_BASE}/api/gaca-audit/checklist`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_BASE}/api/gaca-audit/score`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_BASE}/api/gaca-p1/readiness/linked-capas`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      ]);
      setData(chk);
      setScore(sc);
      setLinkedCapas(lc);
    } catch (e) {
      toast.error('Failed to load GACA checklist');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleState = async (item, key) => {
    const current = item.assessment[key];
    const next = !current;
    setSavingId(item.id);
    try {
      const res = await fetch(`${API_BASE}/api/gaca-audit/assess/${item.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: next }),
      });
      if (!res.ok) throw new Error('save failed');
      // Optimistic update
      setData(prev => ({
        ...prev,
        items: prev.items.map(x =>
          x.id === item.id
            ? { ...x, assessment: { ...x.assessment, [key]: next }, score: ['present','suitable','operating','effective'].reduce((acc,k)=> acc + (k===key ? (next?1:0) : (x.assessment[k]?1:0)), 0) }
            : x
        ),
      }));
      // Refresh score
      fetch(`${API_BASE}/api/gaca-audit/score`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(setScore).catch(()=>{});
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const saveComments = async (itemId, comments) => {
    try {
      await fetch(`${API_BASE}/api/gaca-audit/assess/${itemId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ comments }),
      });
      toast.success('Comment saved');
    } catch {
      toast.error('Save failed');
    }
  };

  const saveHowAchieved = async (itemId, howAchieved) => {
    try {
      await fetch(`${API_BASE}/api/gaca-audit/assess/${itemId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ how_achieved: howAchieved }),
      });
      toast.success('Narrative saved');
    } catch {
      toast.error('Save failed');
    }
  };

  const suggestNarrative = async (itemId, refineExisting) => {
    try {
      const res = await fetch(`${API_BASE}/api/gaca-audit/assess/${itemId}/suggest-narrative`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refine_existing: refineExisting || null }),
      });
      if (res.status === 429) {
        toast.error('Rate limit reached (60/hour). Try again later.');
        return null;
      }
      if (res.status === 503) {
        toast.error('LLM not configured (EMERGENT_LLM_KEY missing)');
        return null;
      }
      if (!res.ok) {
        toast.error('Narrative draft failed');
        return null;
      }
      const data = await res.json();
      toast.success(refineExisting ? 'AI refined your draft' : 'AI drafted a narrative');
      return data;
    } catch {
      toast.error('Narrative draft failed');
      return null;
    }
  };

  const applySuggestion = async (itemId, suggestionId) => {
    try {
      await fetch(`${API_BASE}/api/gaca-audit/assess/${itemId}/suggest-narrative/applied`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ suggestion_id: suggestionId }),
      });
    } catch {
      // best-effort tracking — silent fail
    }
  };

  // -------------------- Bulk drafting --------------------
  const startBulkDraft = async (force = false) => {
    setBulkStarting(true);
    try {
      const res = await fetch(`${API_BASE}/api/gaca-audit/bulk-draft-narratives/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });
      if (res.status === 409) {
        toast.error('You already have an active bulk job. Cancel it first.');
        return;
      }
      if (!res.ok) {
        toast.error('Failed to start bulk draft');
        return;
      }
      const j = await res.json();
      setBulkJob(j);
      setBulkConfirmOpen(false);
      toast.success(`Bulk draft started: ${j.queued} queued, ${j.skipped} skipped`);
    } catch {
      toast.error('Failed to start bulk draft');
    } finally {
      setBulkStarting(false);
    }
  };

  const pollBulkJob = useCallback(async () => {
    if (!bulkJob?.job_id) return;
    try {
      const res = await fetch(`${API_BASE}/api/gaca-audit/bulk-draft-narratives/${bulkJob.job_id}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const j = await res.json();
      setBulkJob(j);
      if (j.state === 'completed' || j.state === 'cancelled') {
        // Refresh checklist to show drafted narratives
        await fetchAll();
        if (j.state === 'completed') {
          toast.success(
            `Bulk draft complete: ${j.completed} drafted, ${j.skipped} skipped, ${j.failed} failed`
          );
        }
      }
    } catch {
      // silent — will retry next interval
    }
  }, [bulkJob?.job_id, token]);

  useEffect(() => {
    if (!bulkJob?.job_id) return;
    if (bulkJob.state !== 'running') return;
    const t = setInterval(pollBulkJob, 3000);
    return () => clearInterval(t);
  }, [bulkJob?.job_id, bulkJob?.state, pollBulkJob]);

  const cancelBulkDraft = async () => {
    if (!bulkJob?.job_id) return;
    try {
      const res = await fetch(`${API_BASE}/api/gaca-audit/bulk-draft-narratives/${bulkJob.job_id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Bulk draft cancelled');
        await pollBulkJob();
      }
    } catch {
      toast.error('Cancel failed');
    }
  };

  const dismissBulkBanner = () => setBulkJob(null);

  // Keyboard shortcut: Cmd/Ctrl+Shift+I → Inspector Mode
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
        e.preventDefault();
        onNavigate?.('inspector-mode');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onNavigate]);

  const openSetup = async () => {
    setSetupOpen(true);
    if (!metadata) {
      try {
        const r = await fetch(`${API_BASE}/api/gaca-audit/metadata`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setMetadata(await r.json());
      } catch {
        toast.error('Failed to load assessment setup');
      }
    }
  };

  const updateMetadataField = (field, value) => {
    setMetadata(prev => ({ ...prev, [field]: value }));
  };

  const updateAssessor = (idx, field, value) => {
    setMetadata(prev => {
      const assessors = [...(prev?.assessors || [])];
      while (assessors.length < 4) assessors.push({ name: '', title: '', signature: '' });
      assessors[idx] = { ...assessors[idx], [field]: value };
      return { ...prev, assessors };
    });
  };

  const saveMetadata = async () => {
    setSavingMetadata(true);
    try {
      const payload = {
        assessment_completion_date: metadata.assessment_completion_date || null,
        certificate_holder_name: metadata.certificate_holder_name || null,
        aoc_number: metadata.aoc_number || null,
        post_holder_name: metadata.post_holder_name || null,
        post_holder_title: metadata.post_holder_title || 'Accountable Executive',
        assessors: metadata.assessors || [],
      };
      const r = await fetch(`${API_BASE}/api/gaca-audit/metadata`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error('save failed');
      const j = await r.json();
      setMetadata(j);
      toast.success('Assessment setup saved');
      setSetupOpen(false);
    } catch {
      toast.error('Save failed');
    } finally {
      setSavingMetadata(false);
    }
  };

  const exportPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/gaca-audit/export.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GACA-SMS-Checklist-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const exportAuditTrailPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/gaca-p1/readiness/audit-trail.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GACA-Readiness-Audit-Trail-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Audit Trail PDF exported');
    } catch {
      toast.error('Audit Trail export failed');
    }
  };

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/gaca-p1/readiness/audit-trail-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('history failed');
      const j = await res.json();
      setHistoryData(j);
    } catch {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }, [token]);

  const openHistory = () => {
    setHistoryOpen(true);
    if (!historyData) fetchHistory();
  };

  const downloadArtifact = async (artifactId, monthLabel) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/gaca-p1/readiness/audit-trail-history/${artifactId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GACA-Readiness-Audit-Trail-${monthLabel}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${monthLabel} artifact`);
    } catch {
      toast.error('Download failed');
    }
  };

  const triggerNow = async () => {
    setTriggering(true);
    try {
      const res = await fetch(`${API_BASE}/api/gaca-p1/readiness/audit-trail/trigger-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Admin / safety_manager only');
        throw new Error('trigger failed');
      }
      const j = await res.json();
      const msg = j.artifacts_created > 0
        ? `Created ${j.artifacts_created} artifact(s) for ${j.year_month}${j.artifacts_emailed ? `, ${j.artifacts_emailed} emailed` : ' (stored — SendGrid not configured)'}`
        : `${j.year_month} already generated (${j.artifacts_skipped_existing} skipped)`;
      toast.success(msg);
      await fetchHistory();
    } catch (e) {
      toast.error(e.message || 'Trigger failed');
    } finally {
      setTriggering(false);
    }
  };

  if (loading || !data || !score) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  const itemsByPillar = PILLAR_ORDER.reduce((acc, p) => {
    acc[p] = data.items.filter(i => i.pillar === p);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="max-w-[1400px] mx-auto space-y-6">
        {/* Bulk-draft progress banner — sticky */}
        {bulkJob && (
          <div
            className={`sticky top-2 z-40 rounded-lg border p-3 backdrop-blur ${
              bulkJob.state === 'running' ? 'bg-violet-950/80 border-violet-500/40' :
              bulkJob.state === 'completed' ? 'bg-emerald-950/80 border-emerald-500/40' :
              'bg-slate-950/80 border-slate-700'
            }`}
            data-testid="bulk-draft-banner"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-1 min-w-[280px]">
                {bulkJob.state === 'running'
                  ? <Loader2 className="w-5 h-5 text-violet-300 animate-spin shrink-0" />
                  : bulkJob.state === 'completed'
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0" />
                    : <X className="w-5 h-5 text-slate-400 shrink-0" />}
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white">
                    {bulkJob.state === 'running' && (
                      <>Drafting {bulkJob.completed} / {bulkJob.total - bulkJob.skipped} narratives…
                      <span className="text-violet-300 ml-2 text-xs font-normal" data-testid="bulk-draft-eta">
                        ETA {bulkJob.eta_seconds > 60 ? `~${Math.round(bulkJob.eta_seconds / 60)}m ${bulkJob.eta_seconds % 60}s` : `~${bulkJob.eta_seconds}s`}
                      </span></>
                    )}
                    {bulkJob.state === 'completed' && (
                      <>Bulk draft complete — {bulkJob.completed} drafted{bulkJob.skipped > 0 && `, ${bulkJob.skipped} skipped`}{bulkJob.failed > 0 && `, ${bulkJob.failed} failed`}</>
                    )}
                    {bulkJob.state === 'cancelled' && <>Bulk draft cancelled — {bulkJob.completed} drafted before cancel</>}
                  </div>
                  {bulkJob.state === 'running' && (
                    <div className="mt-1.5 h-1 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500"
                        style={{ width: `${Math.round(((bulkJob.completed + bulkJob.failed) / Math.max(1, bulkJob.total - bulkJob.skipped)) * 100)}%` }}
                        data-testid="bulk-draft-progress-bar"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {bulkJob.state === 'running' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelBulkDraft}
                    className="border-rose-500/40 text-rose-200 hover:bg-rose-500/10 h-8 text-[11px]"
                    data-testid="bulk-draft-cancel-btn"
                  >
                    <StopCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                  </Button>
                )}
                {bulkJob.state !== 'running' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={dismissBulkBanner}
                    className="text-slate-400 hover:text-slate-200 h-8 text-[11px]"
                    data-testid="bulk-draft-dismiss-btn"
                  >
                    Dismiss
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-sky-500/15 border border-sky-500/30">
                <ShieldCheck className="w-6 h-6 text-sky-300" />
              </div>
              <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="gaca-title">
                {t('gacaAudit.title', 'GACA SMS Audit Mode')}
              </h1>
              <Badge className="bg-sky-600/20 text-sky-300 border border-sky-600/40">
                <Award className="w-3 h-3 mr-1" /> GACAR Part 5
              </Badge>
            </div>
            <p className="text-slate-400 max-w-3xl">
              <span className="font-mono text-xs text-slate-500">{data.document.ref} · {data.document.revision} · {data.document.date}</span>
              <br />
              {t('gacaAudit.subtitle', 'Live, evidence-mapped GACA SMS Effectiveness assessment with one-click PDF export of the populated checklist.')}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => onNavigate?.('inspector-mode')}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white"
              data-testid="gaca-inspector-mode-btn"
            >
              <Eye className="w-4 h-4 mr-2" /> Inspector Mode
              <kbd className="ml-2 px-1.5 py-0.5 rounded bg-black/30 text-[9px] font-mono">⌘⇧I</kbd>
            </Button>
            <Button variant="outline" onClick={openSetup} className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10" data-testid="gaca-setup-btn">
              <ClipboardCheck className="w-4 h-4 mr-2" /> Assessment Setup
            </Button>
            <Button
              variant="outline"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkJob?.state === 'running'}
              className="border-violet-500/40 text-violet-200 hover:bg-violet-500/10"
              data-testid="gaca-bulk-draft-btn"
            >
              <Zap className="w-4 h-4 mr-2" /> Draft Entire Checklist with AI
            </Button>
            <Button variant="outline" onClick={fetchAll} className="border-slate-700 text-slate-200" data-testid="gaca-refresh">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button onClick={exportPdf} className="bg-sky-600 hover:bg-sky-500" data-testid="gaca-export-pdf">
              <FileDown className="w-4 h-4 mr-2" /> Export Filled Checklist (PDF)
            </Button>
          </div>
        </div>

        {/* Overall score card */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
              <div className="md:col-span-1">
                <div className="text-slate-400 text-xs uppercase tracking-wider">Overall maturity</div>
                <div className="mt-1 text-3xl font-bold text-white tabular-nums">{score.overall.pct}%</div>
                <Badge className={`mt-2 ${maturityClass(score.overall.label)}`} data-testid="gaca-overall-maturity">
                  {score.overall.label}
                </Badge>
                <div className="text-xs text-slate-500 mt-2">
                  {score.overall.items_assessed} / {score.overall.items_total} items assessed
                </div>
              </div>
              <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PILLAR_ORDER.map(p => {
                  const pdata = score.pillars[p];
                  if (!pdata) return null;
                  return (
                    <div key={p} className="rounded-lg border border-slate-800 p-3 bg-slate-950/50">
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <span className="text-sm text-slate-200 truncate">{p}</span>
                        <Badge className={`${maturityClass(pdata.label)} text-xs`}>{pdata.label.split(' — ')[0]}</Badge>
                      </div>
                      <Progress value={pdata.pct} className="h-1.5" />
                      <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                        <span>{pdata.assessed}/{pdata.items} assessed</span>
                        <span className="tabular-nums">{pdata.pct}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pillars */}
        {PILLAR_ORDER.map(pillar => {
          const items = itemsByPillar[pillar];
          if (!items?.length) return null;
          const isCollapsed = collapsed[pillar];
          const pscore = score.pillars[pillar];
          return (
            <Card key={pillar} className="bg-slate-900/60 border-slate-800">
              <CardHeader
                className="cursor-pointer"
                onClick={() => setCollapsed(c => ({ ...c, [pillar]: !c[pillar] }))}
                data-testid={`gaca-pillar-${pillar.replace(/[^a-z0-9]/gi, '_')}`}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    {isCollapsed ? <ChevronRight className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
                    {pillar}
                    <Badge className="ml-2 border-slate-700 bg-slate-800 text-slate-300">{items.length} items</Badge>
                  </CardTitle>
                  {pscore && (
                    <Badge className={maturityClass(pscore.label)}>
                      {pscore.label}  ·  {pscore.pct}%
                    </Badge>
                  )}
                </div>
              </CardHeader>
              {!isCollapsed && (
                <CardContent className="space-y-3">
                  {items.map(item => (
                    <ItemRow
                      key={item.id}
                      item={item}
                      onToggle={toggleState}
                      onSaveComments={saveComments}
                      onSaveHowAchieved={saveHowAchieved}
                      onSuggestNarrative={suggestNarrative}
                      onApplySuggestion={applySuggestion}
                      saving={savingId === item.id}
                    />
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })}

        {/* Linked CAPAs from Readiness — audit-trail narrative for inspectors */}
        {linkedCapas && linkedCapas.total > 0 && (
          <Card className="bg-slate-900/60 border-emerald-500/30" data-testid="linked-capas-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-emerald-300" />
                    Linked CAPAs from Readiness
                    <Badge className="bg-emerald-500/15 border-emerald-500/40 text-emerald-200 text-xs ml-1" data-testid="linked-capas-total">
                      {linkedCapas.total} total
                    </Badge>
                  </CardTitle>
                  <CardDescription className="text-slate-400 mt-1">
                    Every CAPA created from a Readiness Action Plan item, with the action's
                    state-at-conversion preserved as audit evidence. Group by month for the
                    inspector-narrative answer to "what did you do about your gaps?"
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <SummaryPill label="Open" value={linkedCapas.summary.open} tone="amber" testid="linked-summary-open" />
                  <SummaryPill label="In progress" value={linkedCapas.summary.in_progress} tone="sky" testid="linked-summary-in-progress" />
                  <SummaryPill label="Closed" value={linkedCapas.summary.closed} tone="emerald" testid="linked-summary-closed" />
                  {linkedCapas.summary.overdue > 0 && (
                    <SummaryPill label="Overdue" value={linkedCapas.summary.overdue} tone="rose" testid="linked-summary-overdue" />
                  )}
                  <Button
                    size="sm"
                    onClick={exportAuditTrailPdf}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white ml-2 h-7 text-[11px]"
                    data-testid="audit-trail-pdf-btn"
                  >
                    <FileDown className="w-3 h-3 mr-1" /> Export Audit Trail PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openHistory}
                    className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 h-7 text-[11px]"
                    data-testid="audit-trail-history-btn"
                  >
                    <History className="w-3 h-3 mr-1" /> History
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-5">
                {linkedCapas.groups.map((g) => (
                  <div key={g.month} data-testid={`linked-month-${g.month}`}>
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-800">
                      <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">{g.label}</h3>
                      <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]">{g.count}</Badge>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                      {g.items.map((it) => {
                        const priorityCls = it.priority === 'high'
                          ? 'bg-rose-500/15 border-rose-500/40 text-rose-200'
                          : it.priority === 'medium'
                          ? 'bg-amber-500/15 border-amber-500/40 text-amber-200'
                          : 'bg-slate-700/40 border-slate-600/40 text-slate-300';
                        const statusCls = it.is_overdue
                          ? 'bg-rose-500/15 border-rose-500/40 text-rose-200'
                          : it.status === 'closed' || it.status === 'completed' || it.status === 'verified'
                          ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
                          : it.status === 'in_progress'
                          ? 'bg-sky-500/15 border-sky-500/40 text-sky-200'
                          : 'bg-amber-500/15 border-amber-500/40 text-amber-200';
                        return (
                          <div
                            key={it.capa_id}
                            data-testid={`linked-capa-${it.capa_number}`}
                            className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 hover:border-slate-700 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-2 mb-1.5 flex-wrap">
                              <div className="flex items-center gap-2">
                                <Badge className={`${priorityCls} text-[10px] uppercase tracking-wider`}>{it.priority}</Badge>
                                <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400 uppercase tracking-wider">{it.category}</Badge>
                              </div>
                              <Badge className={`${statusCls} text-[10px] uppercase tracking-wider`}>
                                {it.is_overdue && <AlertTriangle className="w-3 h-3 mr-1" />}
                                {it.is_overdue ? 'Overdue' : it.status?.replace('_', ' ')}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs text-emerald-300 tabular-nums">{it.capa_number}</span>
                              <span className="text-[10px] text-slate-500">·</span>
                              <span className="text-[11px] text-slate-500">due {(it.due_date || '').slice(0, 10)}</span>
                              {it.assigned_to_name && (
                                <>
                                  <span className="text-[10px] text-slate-500">·</span>
                                  <span className="text-[11px] text-slate-400">{it.assigned_to_name}</span>
                                </>
                              )}
                            </div>
                            <div className="text-sm text-slate-200 leading-snug mb-1">{it.title}</div>
                            <div className="text-[11px] text-slate-500 leading-snug">
                              <span className="opacity-70">At conversion:</span>{' '}
                              <span className="text-slate-300">{it.evidence_at_conversion}</span>
                              {it.target_at_conversion && (
                                <>
                                  {' → '}
                                  <span className="text-emerald-400">{it.target_at_conversion}</span>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit Trail History side-panel */}
        <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
          <SheetContent className="bg-slate-950 border-l border-slate-800 text-slate-100 overflow-y-auto" data-testid="audit-trail-history-sheet">
            <SheetHeader className="pb-4 border-b border-slate-800">
              <SheetTitle className="text-white flex items-center gap-2">
                <Archive className="w-5 h-5 text-emerald-300" />
                Audit Trail History
              </SheetTitle>
              <SheetDescription className="text-slate-400">
                Auto-generated monthly artifacts (1st of each month at 06:00 UTC).
                {historyData && (
                  <span className={`ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${historyData.email_enabled ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-200'}`} data-testid="email-status-badge">
                    {historyData.email_enabled ? <><Mail className="w-3 h-3" /> Email enabled</> : <><Archive className="w-3 h-3" /> Stored only</>}
                  </span>
                )}
              </SheetDescription>
            </SheetHeader>

            <div className="py-4 space-y-4">
              <Button
                onClick={triggerNow}
                disabled={triggering}
                className="w-full bg-sky-600 hover:bg-sky-500 text-white"
                data-testid="audit-trail-trigger-now-btn"
              >
                {triggering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Generate for current month
              </Button>

              {historyLoading && (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                </div>
              )}

              {!historyLoading && historyData && historyData.items.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-sm" data-testid="history-empty">
                  No audit trail artifacts yet. Click <span className="text-sky-400">Generate for current month</span> to create the first one.
                </div>
              )}

              {!historyLoading && historyData && historyData.items.length > 0 && (
                <div className="space-y-2" data-testid="history-list">
                  {historyData.items.map((it) => {
                    const sentBadge = it.delivery_status === 'sent'
                      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                      : 'bg-amber-500/15 border-amber-500/40 text-amber-200';
                    return (
                      <div
                        key={it.id}
                        data-testid={`history-item-${it.year_month}`}
                        className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 hover:border-slate-700 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="text-sm font-semibold text-white">{it.year_month}</div>
                            <div className="text-[11px] text-slate-500">{it.organization_name}</div>
                          </div>
                          <Badge className={`${sentBadge} text-[10px] uppercase tracking-wider`}>
                            {it.delivery_status === 'sent' ? 'Emailed' : 'Stored'}
                          </Badge>
                        </div>
                        <div className="text-[11px] text-slate-400 mb-2 grid grid-cols-2 gap-1">
                          <span>{it.summary_at_generation?.total_capas ?? 0} CAPAs</span>
                          <span className="text-amber-300">{it.summary_at_generation?.open ?? 0} open</span>
                          <span className="text-emerald-300">{it.summary_at_generation?.closed ?? 0} closed</span>
                          {it.summary_at_generation?.overdue > 0 && (
                            <span className="text-rose-300">{it.summary_at_generation.overdue} overdue</span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500 mb-2">
                          Generated {new Date(it.generated_at).toLocaleString()} · {(it.pdf_size_bytes / 1024).toFixed(1)} KB · {it.recipients?.length || 0} recipients
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadArtifact(it.id, it.year_month)}
                          className="w-full border-slate-700 text-slate-200 hover:bg-slate-800 h-7 text-[11px]"
                          data-testid={`history-download-${it.year_month}`}
                        >
                          <FileDown className="w-3 h-3 mr-1" /> Download PDF
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Assessment Setup dialog (cover-page metadata) */}
        <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
          <DialogContent className="bg-slate-950 border border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gaca-setup-dialog">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-amber-300" />
                Assessment Setup
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Cover-page metadata for the GACA-AVSES-SRM-F005 form. Fields populate the PDF cover page when you export the filled checklist.
              </DialogDescription>
            </DialogHeader>

            {!metadata ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Assessment completion date</label>
                    <Input
                      type="date"
                      value={metadata.assessment_completion_date || ''}
                      onChange={(e) => updateMetadataField('assessment_completion_date', e.target.value)}
                      className="bg-slate-900 border-slate-800 text-slate-100"
                      data-testid="gaca-setup-date"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Certificate / AOC Number</label>
                    <Input
                      value={metadata.aoc_number || ''}
                      onChange={(e) => updateMetadataField('aoc_number', e.target.value)}
                      placeholder="e.g. AW044"
                      className="bg-slate-900 border-slate-800 text-slate-100"
                      data-testid="gaca-setup-aoc"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Certificate Holder Name</label>
                    <Input
                      value={metadata.certificate_holder_name || ''}
                      onChange={(e) => updateMetadataField('certificate_holder_name', e.target.value)}
                      placeholder="Operator legal name"
                      className="bg-slate-900 border-slate-800 text-slate-100"
                      data-testid="gaca-setup-holder"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Post Holder Name</label>
                    <Input
                      value={metadata.post_holder_name || ''}
                      onChange={(e) => updateMetadataField('post_holder_name', e.target.value)}
                      placeholder="Accountable Executive's name"
                      className="bg-slate-900 border-slate-800 text-slate-100"
                      data-testid="gaca-setup-postholder"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Post Holder Title</label>
                    <Input
                      value={metadata.post_holder_title || 'Accountable Executive'}
                      onChange={(e) => updateMetadataField('post_holder_title', e.target.value)}
                      className="bg-slate-900 border-slate-800 text-slate-100"
                      data-testid="gaca-setup-postholder-title"
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                    <Award className="w-4 h-4 text-amber-300" /> GACA Assessment Team (up to 4 assessors)
                  </h4>
                  <div className="space-y-2">
                    {[0, 1, 2, 3].map((i) => {
                      const a = (metadata.assessors && metadata.assessors[i]) || { name: '', title: '', signature: '' };
                      return (
                        <div key={i} className="rounded-md border border-slate-800 bg-slate-900/40 p-2.5" data-testid={`gaca-setup-assessor-${i}`}>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Assessor {i + 1}</div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <Input
                              value={a.name || ''}
                              onChange={(e) => updateAssessor(i, 'name', e.target.value)}
                              placeholder="Name"
                              className="bg-slate-950 border-slate-800 text-slate-100 text-sm"
                              data-testid={`gaca-setup-assessor-${i}-name`}
                            />
                            <Input
                              value={a.title || ''}
                              onChange={(e) => updateAssessor(i, 'title', e.target.value)}
                              placeholder="Title"
                              className="bg-slate-950 border-slate-800 text-slate-100 text-sm"
                              data-testid={`gaca-setup-assessor-${i}-title`}
                            />
                            <Input
                              value={a.signature || ''}
                              onChange={(e) => updateAssessor(i, 'signature', e.target.value)}
                              placeholder="Signature (typed)"
                              className="bg-slate-950 border-slate-800 text-slate-100 text-sm"
                              data-testid={`gaca-setup-assessor-${i}-signature`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setSetupOpen(false)} className="border-slate-700 text-slate-200">
                Cancel
              </Button>
              <Button
                onClick={saveMetadata}
                disabled={!metadata || savingMetadata}
                className="bg-amber-600 hover:bg-amber-500 text-white"
                data-testid="gaca-setup-save"
              >
                {savingMetadata ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Save & close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk-draft confirmation alert */}
        <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
          <AlertDialogContent className="bg-slate-950 border border-violet-500/30 text-slate-100" data-testid="bulk-draft-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-violet-300" />
                Draft entire checklist with AI?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                This will draft <b className="text-violet-300">36 narratives</b> via GPT-5.2 in parallel
                (typically ~30 seconds). Items where you've already written more than 50 characters will be
                skipped. Each draft is grounded in live evidence from the database and includes the
                <i> AI-drafted — assessor review required</i> caveat. You'll still need to review each one.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-700 text-slate-200 hover:bg-slate-800">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => startBulkDraft(false)}
                disabled={bulkStarting}
                className="bg-violet-600 hover:bg-violet-500 text-white"
                data-testid="bulk-draft-confirm-btn"
              >
                {bulkStarting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                Yes, draft all 36
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

const SummaryPill = ({ label, value, tone, testid }) => {
  const tones = {
    amber: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
    sky: 'border-sky-500/40 bg-sky-500/10 text-sky-200',
    emerald: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
    rose: 'border-rose-500/40 bg-rose-500/10 text-rose-200',
  };
  return (
    <div className={`px-2 py-1 rounded-md border ${tones[tone] || tones.amber}`} data-testid={testid}>
      <span className="font-bold tabular-nums">{value}</span>
      <span className="ml-1 opacity-70 uppercase tracking-wider text-[10px]">{label}</span>
    </div>
  );
};

const ItemRow = ({ item, onToggle, onSaveComments, onSaveHowAchieved, onSuggestNarrative, onApplySuggestion, saving }) => {
  const [comments, setComments] = useState(item.assessment.comments || '');
  const [howAchieved, setHowAchieved] = useState(item.assessment.how_achieved || '');
  const [showGuidance, setShowGuidance] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const totalEv = (item.evidence || []).reduce((acc, e) => acc + (e.count || 0), 0);

  const handleSuggest = async (refine = false) => {
    setSuggesting(true);
    try {
      const data = await onSuggestNarrative(item.id, refine ? howAchieved : null);
      if (data) setSuggestion(data);
    } finally {
      setSuggesting(false);
    }
  };

  const handleApply = async () => {
    if (!suggestion) return;
    setHowAchieved(suggestion.narrative);
    await onSaveHowAchieved(item.id, suggestion.narrative);
    await onApplySuggestion(item.id, suggestion.suggestion_id);
    setSuggestion(null);
  };

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4" data-testid={`gaca-item-${item.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="font-mono text-xs text-slate-500 mt-1 w-12 shrink-0">{item.id}</div>
        <div className="flex-1 min-w-[280px]">
          <p className="text-sm text-slate-200 leading-relaxed">{item.requirement}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <div className="text-xs text-slate-500 font-mono">{item.gacar}</div>
            {item.assessment.bulk_drafted && (
              <Badge
                variant="outline"
                className="border-violet-500/40 bg-violet-500/10 text-violet-300 text-[9px] uppercase tracking-wider px-1.5 py-0"
                data-testid={`gaca-bulk-drafted-badge-${item.id}`}
              >
                <Sparkles className="w-2.5 h-2.5 mr-0.5" /> AI-drafted · review
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 items-start">
          {STATES.map(s => {
            const checked = !!item.assessment[s.key];
            return (
              <button
                key={s.key}
                type="button"
                disabled={saving}
                onClick={() => onToggle(item, s.key)}
                className={`px-2.5 py-1 rounded-md border text-xs flex items-center gap-1.5 transition-colors ${
                  checked
                    ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                    : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
                data-testid={`gaca-toggle-${item.id}-${s.key}`}
              >
                {checked ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Guidance accordion */}
      {item.guidance && (
        <div className="mt-3" data-testid={`gaca-guidance-wrap-${item.id}`}>
          <button
            type="button"
            onClick={() => setShowGuidance(!showGuidance)}
            className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-sky-300 hover:text-sky-200"
            data-testid={`gaca-guidance-toggle-${item.id}`}
          >
            {showGuidance ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Assessor guidance
          </button>
          {showGuidance && (
            <div
              className="mt-1.5 rounded-md border border-sky-500/20 bg-sky-500/5 p-3 text-xs text-slate-300 leading-relaxed whitespace-pre-line"
              data-testid={`gaca-guidance-${item.id}`}
            >
              {item.guidance}
            </div>
          )}
        </div>
      )}

      {/* Evidence bar */}
      {item.evidence && item.evidence.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-500 mt-1.5 mr-1">
            <Sparkles className="w-3 h-3 inline mr-1" /> Live evidence ({totalEv}):
          </span>
          {item.evidence.map((e, i) => (
            <TooltipProvider key={i} delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className={`border-slate-700 ${e.count > 0 ? 'text-sky-300 bg-sky-500/10' : 'text-slate-500'}`}
                    data-testid={`gaca-evidence-${item.id}-${i}`}
                  >
                    <span className="tabular-nums mr-1.5">{e.count}</span>
                    {e.label}
                    <ExternalLink className="w-2.5 h-2.5 ml-1.5 opacity-70" />
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="bg-slate-900 border-slate-800 text-slate-200">
                  Reference: {e.link}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      )}

      {/* How it is achieved */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-[11px] uppercase tracking-wider text-emerald-300">How it is achieved</label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={suggesting}
            onClick={() => handleSuggest(!!howAchieved)}
            className="h-6 px-2 border-violet-500/40 text-violet-200 hover:bg-violet-500/10 text-[10px]"
            data-testid={`gaca-suggest-narrative-${item.id}`}
          >
            {suggesting
              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Drafting…</>
              : <><Wand2 className="w-3 h-3 mr-1" /> {howAchieved ? 'Refine with AI' : 'Suggest narrative'}</>
            }
          </Button>
        </div>
        <Textarea
          value={howAchieved}
          onChange={(e) => setHowAchieved(e.target.value)}
          onBlur={() => {
            if (howAchieved !== (item.assessment.how_achieved || '')) onSaveHowAchieved(item.id, howAchieved);
          }}
          placeholder="Narrative evidence: how does the organisation actually achieve this? (procedures, records, examples)"
          rows={2}
          className="bg-slate-950 border-slate-800 text-slate-100 text-sm"
          data-testid={`gaca-how-achieved-${item.id}`}
        />

        {/* AI suggestion preview card */}
        {suggestion && (
          <div
            className="mt-2 rounded-md border border-violet-500/30 bg-violet-500/5 p-3"
            data-testid={`gaca-suggestion-preview-${item.id}`}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-violet-300">
                <Sparkles className="w-3 h-3" /> AI draft · {suggestion.model} {suggestion.is_refine ? '· refined' : ''}
              </div>
              <button
                type="button"
                onClick={() => setSuggestion(null)}
                className="text-slate-500 hover:text-slate-300"
                data-testid={`gaca-suggestion-dismiss-${item.id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div
              className="text-xs text-slate-200 leading-relaxed whitespace-pre-line max-h-48 overflow-y-auto"
              data-testid={`gaca-suggestion-text-${item.id}`}
            >
              {suggestion.narrative}
            </div>
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                onClick={handleApply}
                className="h-7 bg-violet-600 hover:bg-violet-500 text-white text-[11px]"
                data-testid={`gaca-suggestion-apply-${item.id}`}
              >
                <Check className="w-3 h-3 mr-1" /> Apply to draft
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={suggesting}
                onClick={() => handleSuggest(true)}
                className="h-7 border-slate-700 text-slate-200 text-[11px]"
                data-testid={`gaca-suggestion-refine-${item.id}`}
              >
                <Wand2 className="w-3 h-3 mr-1" /> Refine again
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Comments */}
      <div className="mt-2">
        <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Comments</label>
        <Textarea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          onBlur={() => {
            if (comments !== (item.assessment.comments || '')) onSaveComments(item.id, comments);
          }}
          placeholder="Assessor comments / evidence references..."
          rows={2}
          className="bg-slate-950 border-slate-800 text-slate-100 text-sm"
          data-testid={`gaca-comment-${item.id}`}
        />
      </div>
    </div>
  );
};
